import { forwardRef, ReactNode } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import styles from "./SampleListTitleResultCard.module.scss";
import { clsx } from "clsx";
import { DirectoryProduct } from "common/api/decimal/methods/samplingContent.ts";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton";
import ListAssetCardContainer from "common/features/asset/ListAssetCardContainer";
import EmptyAssetCard from "common/features/asset/EmptyAssetCard";
import SampleFeatureTag from "common/features/asset/SampleFeatureTag";
import AssetTypeTags from "common/features/asset/AssetTypeTags";
import NthEdition from "common/features/asset/NthEdition";
import { useSampleSearchLog } from "../useSampleSearchLog.ts";
import { getSearchQuery } from "../../query.ts";
import PublisherInfo from "routes/home/common/product/PublisherInfo.tsx";
import NewerEditionAvailable from "common/features/asset/NewerEditionAvailable.tsx";
import TrendingUpBadgeLabel from "common/features/asset/TrendingUpBadgeLabel.tsx";

function SampleListTitleResultCard(
  {
    asset,
    lazyLoad,
    programName,
  }: {
    asset: DirectoryProduct;
    lazyLoad?: boolean;
    programName?: string | null;
  },
  ref: React.ForwardedRef<HTMLElement>
) {
  const intl = useIntl();
  const log = useSampleSearchLog();
  const [searchParams] = useSearchParams();
  const query = getSearchQuery(searchParams);
  const pdpLink = `/search/faculty-products/${asset.vbid}`;

  // Track View Details click
  const handleViewDetailsClick = () => {
    log("sample_search_view_details", {
      vbid: asset.vbid,
      titleName: asset.title,
      query: query ?? "",
    });
  };

  // Build topRow with format tags
  const topRow = <AssetTypeTags asset={asset} className={styles.tags} />;

  // Build buttons for PDP link
  const buttons = (
    <>
      {asset.priceStartingAt && (
        <div className={styles.price}>
          <div className={styles.priceLabel}>
            <FormattedMessage
              defaultMessage="Student Price Available at: <span>{price}</span>"
              values={{
                price:
                  asset.priceStartingAt === "FREE"
                    ? intl.formatMessage({ defaultMessage: "Free" })
                    : asset.priceStartingAt,
                span: (chunks: ReactNode) => (
                  <span className={styles.priceValue}>{chunks}</span>
                ),
              }}
            />
          </div>
        </div>
      )}

      <BaseLinkOrButton
        className="vst-button-pill"
        to={pdpLink}
        onClick={handleViewDetailsClick}
        aria-label={intl.formatMessage(
          {
            defaultMessage: "View Details for {title}",
          },
          { title: asset.title }
        )}
      >
        <FormattedMessage defaultMessage="View Details" />
      </BaseLinkOrButton>
    </>
  );

  // Render card directly using shared layout components
  // Works with DirectoryProduct directly - no transformation needed
  return (
    <ListAssetCardContainer tagName="li" className={styles.container}>
      <div
        className={styles.cardWrapper}
        ref={ref as React.LegacyRef<HTMLDivElement>}
      >
        <EmptyAssetCard
          type="horizontal"
          vbid={asset.vbid}
          tagName="div"
          lazyLoad={lazyLoad}
          className={styles.card}
          primaryAction={{ to: pdpLink }}
          imageAddendum={
            asset.trending && (
              <TrendingUpBadgeLabel className={styles.trendingBadge} />
            )
          }
        >
          {topRow}
          <div className={styles.innerContents}>
            <div className={styles.bookDetails}>
              <div className={styles.title}>{asset.title}</div>
              {asset.subtitle && (
                <div className={styles.subtitle}>{asset.subtitle}</div>
              )}
              <div className={styles.editionRow}>
                <NthEdition asset={asset} className={styles.edition} />
                <NewerEditionAvailable asset={asset} />
              </div>
              <div className={styles.authorPublisher}>
                <div>{asset.author}</div>
                <PublisherInfo asset={asset} />
              </div>
              {asset.trending && (
                <TrendingUpBadgeLabel
                  className={styles.trendingBadgeMobileView}
                />
              )}
              <SampleFeatureTag asset={asset} programName={programName} />
            </div>
          </div>
        </EmptyAssetCard>
        <div className={clsx(styles.buttons, styles.bottomButtons)}>
          {buttons}
        </div>
      </div>
    </ListAssetCardContainer>
  );
}

const Card = forwardRef(SampleListTitleResultCard);

export default Card;
